/**
 * useReturnShipment - Hook para devolver un shipment a pedidos
 *
 * Se encarga de llamar al endpoint correspondiente y mostrar feedback
 * al usuario mediante toasts. Aplica el patrón estándar del proyecto:
 * - Estado de loading local
 * - Toast de éxito con mensaje de la API
 * - Toast de error con mensaje detallado
 * - Logs de depuración en cada capa
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@shared/hooks/use-toast';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';

interface ReturnShipmentParams {
  preInvoiceId: number;
  shipmentId: number;
  onSuccess?: () => void;
}

export const useReturnShipment = () => {
  const { t } = useTranslation('viewPreInvoicesModule');
  const { toast } = useToast();
  const [isReturning, setIsReturning] = useState(false);

  const returnShipment = async ({
    preInvoiceId,
    shipmentId,
    onSuccess,
  }: ReturnShipmentParams) => {
    setIsReturning(true);

    try {
      console.log('🔍 useReturnShipment - Iniciando devolución a pedidos:', {
        preInvoiceId,
        shipmentId,
      });

      const httpClient = createHttpClient(apiClientWH);
      const response = await httpClient.post(
        `/invoices/pre-invoices/${preInvoiceId}/shipments/${shipmentId}`,
        {}
      );

      console.log('🔍 useReturnShipment - Respuesta de la API:', response);

      toast({
        title: t('toast.return_success.title', 'Shipment devuelto a pedidos'),
        description:
          response?.message ||
          t(
            'toast.return_success.description',
            'El shipment se ha devuelto a pedidos correctamente'
          ),
        variant: 'default',
      });

      onSuccess?.();
    } catch (error: any) {
      console.log('🔍 useReturnShipment - Error en la API:', error);

      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        error?.message ||
        t(
          'toast.return_error.unexpected',
          'Error inesperado al devolver el shipment. Inténtalo de nuevo.'
        );

      toast({
        title: t('toast.return_error.title', 'Error al devolver shipment'),
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsReturning(false);
    }
  };

  return {
    returnShipment,
    isReturning,
  };
};
